<template>
  <div class="deal-activities">
    <!-- Composer -->
    <div class="deal-activities__composer">
      <div class="deal-activities__composer-row">
        <span class="deal-activities__composer-avatar">{{ currentUserInitials }}</span>
        <input
          ref="quickInputRef"
          type="text"
          v-model="quickNote"
          :placeholder="$t('deals.add_note_placeholder')"
          class="deal-activities__input"
          @keydown.enter="submitQuickNote"
        />
      </div>

      <div class="deal-activities__composer-divider"></div>

      <div class="deal-activities__composer-actions">
        <button
          v-for="type in quickActivityTypes"
          :key="type.value"
          type="button"
          class="deal-activities__chip"
          :class="{ 'deal-activities__chip--active': selectedType === type.value }"
          :aria-pressed="selectedType === type.value"
          @click="selectedType = type.value"
        >
          <BaseIcon :name="type.icon" size="14" />
          <span class="deal-activities__chip-label">{{ type.label }}</span>
        </button>

        <div class="deal-activities__composer-spacer"></div>

        <button
          v-if="dealEmailEnabled"
          type="button"
          class="deal-activities__chip"
          :title="$t('deals.mail_compose')"
          :aria-label="$t('deals.mail_compose')"
          @click="openCompose"
        >
          <BaseIcon name="paper-plane" size="14" />
        </button>

        <button
          type="button"
          class="deal-activities__save"
          :disabled="!quickNote.trim()"
          @click="submitQuickNote"
        >
          {{ $t('deals.save_activity') }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div class="deal-activities__loading" v-if="loading">
      <div class="deal-activities__spinner"></div>
    </div>

    <!-- Empty State -->
    <div class="deal-activities__empty" v-else-if="activities.length === 0">
      <BaseIcon name="activity" size="32" />
      <p>{{ $t('deals.no_activities') }}</p>
    </div>

    <!-- Timeline -->
    <div class="deal-activities__timeline" v-else>
      <template v-for="group in groupedActivities" :key="group.key">
        <div class="deal-activities__day">{{ group.label }}</div>

        <div
          v-for="(activity, index) in group.items"
          :key="activity.id"
          class="deal-activities__item"
          :class="{
            'deal-activities__item--last':
              group === groupedActivities[groupedActivities.length - 1] &&
              index === group.items.length - 1,
          }"
        >
          <div class="deal-activities__rail">
            <div class="deal-activities__rail-icon">
              <BaseIcon :name="timelineIcon(activity)" size="14" />
            </div>
            <div class="deal-activities__rail-line"></div>
          </div>

          <div class="deal-activities__content">
            <div class="deal-activities__row">
              <!-- System event -->
              <template v-if="isSystemEvent(activity)">
                <span class="deal-activities__title">{{ systemTitle(activity) }}</span>
                <span class="deal-activities__meta">{{ systemMeta(activity) }}</span>
                <button
                  v-if="submissionLink(activity)"
                  type="button"
                  class="deal-activities__link"
                  @click="openSubmission(activity)"
                >
                  {{ $t('deals.timeline.view_submission') }}
                </button>
              </template>

              <!-- User / mail activity -->
              <template v-else>
                <span v-if="authorInitials(activity)" class="deal-activities__author-avatar">
                  {{ authorInitials(activity) }}
                </span>
                <span class="deal-activities__title">
                  {{ activityAuthor(activity) || activity.subject }}
                </span>
                <span v-if="typeBadge(activity)" class="deal-activities__badge">
                  {{ typeBadge(activity) }}
                </span>
                <span
                  v-if="activity.outcome"
                  class="deal-activities__badge"
                  :class="`deal-activities__badge--${activity.outcome}`"
                >
                  {{ getOutcomeLabel(activity.outcome) }}
                </span>
                <span
                  v-if="activity.metadata?.delivery_status === 'bounced'"
                  class="deal-activities__badge deal-activities__badge--negative"
                >
                  {{ $t('deals.mail_bounced') }}
                </span>
              </template>

              <div class="deal-activities__row-spacer"></div>

              <button
                v-if="!activity.is_auto_generated"
                type="button"
                class="deal-activities__delete"
                :title="$t('common.delete')"
                :aria-label="$t('common.delete')"
                @click="handleDelete(activity.id)"
              >
                <BaseIcon name="trash" size="12" />
              </button>
              <span class="deal-activities__time">{{ timeAgo(activity.created_at) }}</span>
            </div>

            <!-- Note / mail body -->
            <div v-if="hasCard(activity)" class="deal-activities__card">
              <p
                v-if="isMailActivity(activity) || activityAuthor(activity)"
                class="deal-activities__card-subject"
                :class="{ 'deal-activities__card-subject--strong': isMailActivity(activity) }"
              >
                {{ activity.subject }}
              </p>
              <p class="deal-activities__card-text" v-if="activityDescription(activity)">
                {{ activityDescription(activity) }}
              </p>
              <div v-if="mailImages(activity).length" class="deal-activities__images">
                <a
                  v-for="image in mailImages(activity)"
                  :key="image.url"
                  :href="image.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="deal-activities__image-link"
                >
                  <img :src="image.url" :alt="image.filename || $t('deals.mail_photo')" />
                </a>
              </div>
              <div
                v-if="canViewMail(activity) || canReply(activity)"
                class="deal-activities__card-actions"
              >
                <button
                  v-if="canViewMail(activity)"
                  type="button"
                  class="deal-activities__link"
                  @click="openThread(activity)"
                >
                  {{ $t('deals.mail_view') }}
                </button>
                <button
                  v-if="canReply(activity)"
                  type="button"
                  class="deal-activities__link"
                  @click="openReply(activity)"
                >
                  {{ $t('deals.mail_reply') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Log Activity Modal (for extended logging) -->
    <div class="deal-activities__modal-overlay" v-if="showLogModal">
      <div class="deal-activities__modal">
        <div class="deal-activities__modal-header">
          <h4>{{ $t('deals.log_activity') }}</h4>
          <button @click="showLogModal = false">
            <BaseIcon name="xmark" size="16" />
          </button>
        </div>

        <div class="deal-activities__modal-body">
          <div class="deal-activities__form-group">
            <label>{{ $t('deals.activity_type') }}</label>
            <div class="deal-activities__type-buttons">
              <button
                v-for="type in activityTypes"
                :key="type.value"
                class="deal-activities__type-btn"
                :class="{ 'deal-activities__type-btn--active': newActivity.type === type.value }"
                @click="newActivity.type = type.value"
              >
                <BaseIcon :name="type.icon" size="16" />
                <span>{{ type.label }}</span>
              </button>
            </div>
          </div>

          <div class="deal-activities__form-group">
            <label>{{ $t('deals.activity_subject') }}</label>
            <TextInput
              id="activity-subject"
              v-model="newActivity.subject"
              :placeholder="$t('deals.activity_subject_placeholder')"
            />
          </div>

          <div class="deal-activities__form-group">
            <label>{{ $t('deals.activity_description') }}</label>
            <textarea
              v-model="newActivity.description"
              :placeholder="$t('deals.activity_description_placeholder')"
              rows="3"
            ></textarea>
          </div>

          <div class="deal-activities__form-group">
            <label>{{ $t('deals.activity_outcome') }}</label>
            <div class="deal-activities__outcome-buttons">
              <button
                v-for="outcome in outcomes"
                :key="outcome.value"
                class="deal-activities__outcome-btn"
                :class="{
                  'deal-activities__outcome-btn--active': newActivity.outcome === outcome.value,
                  [`deal-activities__outcome-btn--${outcome.value}`]: true,
                }"
                @click="newActivity.outcome = outcome.value"
              >
                {{ outcome.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="deal-activities__modal-footer">
          <Btn variant="secondary" size="sm" @click="showLogModal = false">
            {{ $t('common.cancel') }}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            @click="handleLogActivity"
            :disabled="!newActivity.subject"
          >
            {{ $t('deals.save_activity') }}
          </Btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import dayjs from 'dayjs';
  import { useDealStore } from '@/store/dealStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import DealEmailReplyModal from '@/components/deals/DealEmailReplyModal.vue';
  import DealEmailThreadModal from '@/components/deals/DealEmailThreadModal.vue';
  import { isDealEmailEnabled } from '@/services/dealEmailService';
  import { quotedReplyPlaintext } from '@/utils/quotedReplyExcerpt';

  export default {
    name: 'DealActivities',

    components: {
      Btn,
      BaseIcon,
      TextInput,
    },

    props: {
      dealId: {
        type: String,
        required: true,
      },
    },

    setup(props, { expose }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const $i18n = instance.appContext.config.globalProperties.$i18n;
      const dealStore = useDealStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const router = useRouter();

      // Quick input state
      const quickInputRef = ref(null);
      const quickNote = ref('');
      const selectedType = ref('note');
      const showQuickActions = ref(false);

      // Modal state
      const showLogModal = ref(false);
      const newActivity = ref({
        type: 'call',
        subject: '',
        description: '',
        outcome: null,
      });

      const activities = computed(() => dealStore.dealActivities);
      const loading = computed(() => dealStore.dealActivitiesLoading);

      const currentUserInitials = computed(() => {
        const user = userStore.user;
        if (!user) return '';
        const firstName = user.user_metadata?.first_name || userStore.userData?.first_name || '';
        const lastName = user.user_metadata?.last_name || userStore.userData?.last_name || '';
        const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
        return initials || (user.email || '').charAt(0).toUpperCase();
      });

      const quickActivityTypes = [
        { value: 'note', label: $t('deals.quick_types.note'), icon: 'edit' },
        { value: 'call', label: $t('deals.quick_types.call'), icon: 'phone' },
        { value: 'email', label: $t('deals.quick_types.email'), icon: 'mail' },
        { value: 'meeting', label: $t('deals.quick_types.meeting'), icon: 'calendar' },
      ];

      const activityTypes = [
        { value: 'call', label: $t('deals.activity_types.call'), icon: 'phone' },
        { value: 'email', label: $t('deals.activity_types.email'), icon: 'mail' },
        { value: 'whatsapp', label: $t('deals.activity_types.whatsapp'), icon: 'message-circle' },
        { value: 'meeting', label: $t('deals.activity_types.meeting'), icon: 'calendar' },
        { value: 'note', label: $t('deals.activity_types.note'), icon: 'file-text' },
      ];

      const outcomes = [
        { value: 'positive', label: $t('deals.outcomes.positive') },
        { value: 'neutral', label: $t('deals.outcomes.neutral') },
        { value: 'negative', label: $t('deals.outcomes.negative') },
        { value: 'no_answer', label: $t('deals.outcomes.no_answer') },
      ];

      const getActivityIcon = (type) => {
        const icons = {
          call: 'phone',
          email: 'envelope',
          email_sent: 'paper-plane',
          whatsapp: 'message-circle',
          note: 'file-text',
          meeting: 'calendar',
          stage_change: 'arrow-right',
          deal_created: 'plus-circle',
          deal_won: 'trophy',
          deal_lost: 'x-circle',
        };
        return icons[type] || 'activity';
      };

      const isMailActivity = (activity) =>
        !!activity?.metadata?.thread_id ||
        activity?.type === 'email' ||
        activity?.type === 'email_sent';

      // Falls back to the logged-in user for activities we just created
      // (the create response has no user relation yet).
      const activityUser = (activity) => {
        const user = activity?.user || activity?.created_by_user;
        if (user) return user;
        if (activity?.is_auto_generated) return null;
        const me = userStore.userData;
        if (me && activity?.created_by && activity.created_by === me.id) return me;
        return null;
      };

      const activityAuthor = (activity) => {
        if (activity?.metadata?.from_name) return activity.metadata.from_name;
        if (activity?.metadata?.from_email) return activity.metadata.from_email;
        const user = activityUser(activity);
        if (user) {
          return `${user.first_name || ''} ${user.last_name || ''}`.trim();
        }
        if (activity?.is_auto_generated) return $t('deals.system');
        return '';
      };

      const activityDescription = (activity) => {
        if (!activity?.description) return '';
        if (!isMailActivity(activity)) return activity.description;
        return quotedReplyPlaintext(activity.description, '') || activity.description;
      };

      const getOutcomeLabel = (outcome) => {
        const labels = {
          positive: $t('deals.outcomes.positive'),
          neutral: $t('deals.outcomes.neutral'),
          negative: $t('deals.outcomes.negative'),
          no_answer: $t('deals.outcomes.no_answer'),
        };
        return labels[outcome] || outcome;
      };

      // ------------------------------------------------------------------
      // Timeline helpers
      // ------------------------------------------------------------------

      const SYSTEM_EVENT_ICONS = {
        deal_created: 'plus',
        deal_stage_changed: 'arrow-right',
        deal_won: 'check',
        deal_lost: 'xmark',
        deal_reopened: 'activity',
        submission_received: 'inbox',
      };

      const isSystemEvent = (activity) =>
        !!activity?.is_auto_generated && !isMailActivity(activity);

      const timelineIcon = (activity) => {
        const event = activity?.metadata?.event;
        if (isSystemEvent(activity) && SYSTEM_EVENT_ICONS[event]) {
          return SYSTEM_EVENT_ICONS[event];
        }
        return getActivityIcon(activity?.type);
      };

      const sourceLabel = (source) => {
        const key = `deals.timeline.source.${source}`;
        const label = source ? $t(key) : '';
        return label && label !== key ? label : source || '';
      };

      const systemTitle = (activity) => {
        const meta = activity?.metadata || {};
        if (meta.event === 'deal_stage_changed') {
          return meta.to_stage
            ? $t('deals.timeline.stage_to', { stage: meta.to_stage })
            : activity.subject;
        }
        return activity?.subject || '';
      };

      const systemMeta = (activity) => {
        const meta = activity?.metadata || {};
        const parts = [];
        if (meta.event === 'deal_stage_changed' && meta.from_stage) {
          parts.push($t('deals.timeline.stage_from', { stage: meta.from_stage }));
        } else if (meta.event === 'deal_created' && meta.source) {
          parts.push($t('deals.timeline.created_via', { source: sourceLabel(meta.source) }));
        } else if (meta.event === 'submission_received' && meta.form_name) {
          parts.push($t('deals.timeline.created_via', { source: meta.form_name }));
        } else if (activity?.description && meta.event !== 'deal_stage_changed') {
          parts.push(activity.description);
        }
        parts.push($t('deals.system'));
        return parts.join(' · ');
      };

      const submissionLink = (activity) => {
        const meta = activity?.metadata || {};
        let submissionId = null;
        if (meta.event === 'submission_received') submissionId = meta.submission_id;
        if (meta.event === 'deal_created') {
          submissionId = dealStore.currentDeal?.source_submission_id || null;
        }
        if (!submissionId) return null;
        const submission = dealStore.dealSubmissions.find((item) => item.id === submissionId);
        const formId = submission?.form?.id || submission?.form_id;
        return formId ? `/flow/${formId}/submissions/${submissionId}` : null;
      };

      const openSubmission = (activity) => {
        const link = submissionLink(activity);
        if (link) router.push(link);
      };

      const authorInitials = (activity) => {
        if (isMailActivity(activity)) return '';
        const user = activityUser(activity);
        if (!user) return '';
        const initials = `${(user.first_name || '').charAt(0)}${(user.last_name || '').charAt(0)}`;
        return initials.toUpperCase();
      };

      const typeBadge = (activity) => {
        if (isMailActivity(activity) || !activity?.type || activity.type === 'note') return '';
        const match =
          quickActivityTypes.find((item) => item.value === activity.type) ||
          activityTypes.find((item) => item.value === activity.type);
        return match?.label || '';
      };

      const hasCard = (activity) => {
        if (isSystemEvent(activity)) return false;
        if (activityDescription(activity) || mailImages(activity).length) return true;
        if (canViewMail(activity) || canReply(activity)) return true;
        // Without an author the subject is already shown as the row title
        return !!activity?.subject && (isMailActivity(activity) || !!activityAuthor(activity));
      };

      const timeAgo = (date) => {
        if (!date) return '';
        const now = dayjs();
        const then = dayjs(date);
        const minutes = now.diff(then, 'minute');
        if (minutes < 1) return $t('deals.timeline.just_now');
        if (minutes < 60) return $t('deals.timeline.minutes_ago', { count: minutes });
        const hours = now.diff(then, 'hour');
        if (hours === 1) return $t('deals.timeline.one_hour_ago');
        if (hours < 24) return $t('deals.timeline.hours_ago', { count: hours });
        const days = now.diff(then, 'day');
        if (days === 1) return $t('deals.timeline.one_day_ago');
        if (days < 7) return $t('deals.timeline.days_ago', { count: days });
        return formatDay(then, { day: 'numeric', month: 'short', year: 'numeric' });
      };

      const currentLocale = () => {
        const locale = $i18n?.locale;
        return (typeof locale === 'string' ? locale : locale?.value) || 'nl-NL';
      };

      const formatDay = (day, options) =>
        new Intl.DateTimeFormat(currentLocale(), options).format(day.toDate());

      const groupedActivities = computed(() => {
        const today = dayjs().startOf('day');
        const groups = [];
        const byKey = new Map();

        activities.value.forEach((activity) => {
          const day = dayjs(activity.created_at).startOf('day');
          const key = day.format('YYYY-MM-DD');
          if (!byKey.has(key)) {
            let label;
            if (day.isSame(today, 'day')) label = $t('common.today');
            else if (day.isSame(today.subtract(1, 'day'), 'day')) label = $t('common.yesterday');
            else
              label = formatDay(day, {
                day: 'numeric',
                month: 'long',
                ...(day.year() !== today.year() ? { year: 'numeric' } : {}),
              });
            const group = { key, label, items: [] };
            byKey.set(key, group);
            groups.push(group);
          }
          byKey.get(key).items.push(activity);
        });

        return groups;
      });

      const submitQuickNote = async () => {
        if (!quickNote.value.trim()) return;

        try {
          await dealStore.createDealActivity(props.dealId, {
            type: selectedType.value,
            subject: quickNote.value.trim(),
            description: null,
            outcome: null,
          });

          toastStore.addToast({
            type: 'success',
            message: $t('deals.activity_logged'),
          });

          // Reset
          quickNote.value = '';
          selectedType.value = 'note';
          showQuickActions.value = false;
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.activity_log_failed'),
          });
        }
      };

      const handleLogActivity = async () => {
        if (!newActivity.value.subject) return;

        try {
          await dealStore.createDealActivity(props.dealId, {
            type: newActivity.value.type,
            subject: newActivity.value.subject,
            description: newActivity.value.description || null,
            outcome: newActivity.value.outcome,
          });

          toastStore.addToast({
            type: 'success',
            message: $t('deals.activity_logged'),
          });

          // Reset form
          newActivity.value = {
            type: 'call',
            subject: '',
            description: '',
            outcome: null,
          };
          showLogModal.value = false;
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.activity_log_failed'),
          });
        }
      };

      const handleDelete = async (activityId) => {
        if (!confirm($t('deals.delete_activity_confirm'))) return;

        try {
          await dealStore.deleteActivity(activityId);
          await dealStore.fetchDealActivities(props.dealId);

          toastStore.addToast({
            type: 'success',
            message: $t('deals.activity_deleted'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.activity_delete_failed'),
          });
        }
      };

      const dealEmailEnabled = computed(() => isDealEmailEnabled(userStore.currentOrganization));

      const canReply = (activity) =>
        dealEmailEnabled.value &&
        !!activity?.metadata?.thread_id &&
        activity.metadata?.direction === 'inbound';

      const canViewMail = (activity) => dealEmailEnabled.value && !!activity?.metadata?.thread_id;

      const INLINE_IMAGE_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
      ];

      const mailImages = (activity) =>
        (activity?.metadata?.attachments || []).filter(
          (file) =>
            file?.url &&
            file.inline !== false &&
            INLINE_IMAGE_TYPES.includes(String(file.content_type || '').toLowerCase())
        );

      const refreshActivities = () => dealStore.fetchDealActivities(props.dealId);

      const openReply = (activity) => {
        uiStore.addModal(DealEmailReplyModal, {
          title: $t('deals.mail_reply'),
          size: 'md',
          dealId: props.dealId,
          threadId: activity.metadata.thread_id,
          initialSubject: activity.subject || '',
          onSent: refreshActivities,
        });
      };

      const openThread = (activity) => {
        uiStore.addModal(DealEmailThreadModal, {
          title: activity.subject || $t('deals.mail_view'),
          size: 'lg',
          dealId: props.dealId,
          threadId: activity.metadata.thread_id,
          initialSubject: activity.subject || '',
          onSent: refreshActivities,
        });
      };

      const openCompose = () => {
        const deal = dealStore.currentDeal?.id === props.dealId ? dealStore.currentDeal : null;
        uiStore.addModal(DealEmailReplyModal, {
          title: $t('deals.mail_compose'),
          size: 'md',
          dealId: props.dealId,
          recipient: deal?.lead?.email || deal?.contact?.email || null,
          onSent: refreshActivities,
        });
      };

      onMounted(async () => {
        await Promise.all([
          dealStore.fetchDealActivities(props.dealId),
          dealStore.fetchDealSubmissions(props.dealId).catch(() => {}),
        ]);
      });

      const focusInput = () => {
        showQuickActions.value = true;
        quickInputRef.value?.focus();
      };

      expose({ focusInput });

      return {
        quickInputRef,
        quickNote,
        selectedType,
        showQuickActions,
        showLogModal,
        newActivity,
        activities,
        loading,
        currentUserInitials,
        quickActivityTypes,
        activityTypes,
        outcomes,
        getActivityIcon,
        getOutcomeLabel,
        groupedActivities,
        isSystemEvent,
        isMailActivity,
        timelineIcon,
        systemTitle,
        systemMeta,
        submissionLink,
        openSubmission,
        authorInitials,
        typeBadge,
        hasCard,
        timeAgo,
        submitQuickNote,
        handleLogActivity,
        handleDelete,
        mailImages,
        canReply,
        canViewMail,
        dealEmailEnabled,
        activityAuthor,
        activityDescription,
        openReply,
        openThread,
        openCompose,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-activities {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;

    // ------------------------------------------------------------------
    // Composer
    // ------------------------------------------------------------------
    &__composer {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      transition: border-color 0.15s ease;

      &:focus-within {
        border-color: var(--brand-500);
      }
    }

    &__composer-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__composer-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: var(--brand-100);
      color: var(--brand-600);
      font-size: 0.625rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }

    &__input {
      flex: 1;
      min-width: 0;
      padding: 0;
      border: none;
      background: transparent;
      font-size: 0.8125rem;
      line-height: 1.25rem;
      color: var(--slate-900);

      &::placeholder {
        color: var(--slate-500);
      }

      &:focus {
        outline: none;
      }
    }

    &__composer-divider {
      height: 1px;
      background: var(--slate-200);
    }

    &__composer-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      flex-wrap: wrap;
    }

    &__composer-spacer {
      flex: 1;
    }

    &__chip {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      height: 28px;
      padding: 0 0.5rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      font-size: 0.8125rem;
      font-weight: 500;
      line-height: 1;
      color: var(--slate-700);
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;

      @include hover {
        &:hover {
          background: var(--slate-100);
          border-color: var(--slate-300);
        }
      }

      &--active {
        background: var(--slate-200);
        border-color: var(--slate-200);
        color: var(--slate-900);
      }
    }

    &__chip-label {
      @include lg-down {
        display: none;
      }
    }

    &__save {
      display: inline-flex;
      align-items: center;
      height: 28px;
      padding: 0 0.75rem;
      background: var(--slate-900);
      border: 1px solid var(--slate-900);
      border-radius: 4px;
      font-size: 0.8125rem;
      font-weight: 500;
      color: white;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        opacity 0.15s ease;

      @include hover {
        &:hover:not(:disabled) {
          background: var(--slate-800);
        }
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    // ------------------------------------------------------------------
    // States
    // ------------------------------------------------------------------
    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    &__spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--slate-200);
      border-top-color: var(--brand-500);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    &__empty {
      text-align: center;
      padding: 2rem;
      color: var(--slate-400);

      p {
        margin: 0.5rem 0 0 0;
        font-size: 0.8125rem;
      }
    }

    // ------------------------------------------------------------------
    // Timeline
    // ------------------------------------------------------------------
    &__timeline {
      display: flex;
      flex-direction: column;
    }

    &__day {
      padding: 0 0 0.5rem 2.5rem;
      font-size: 0.625rem;
      font-weight: 500;
      line-height: 1.25rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--slate-500);

      &:not(:first-child) {
        padding-top: 0.25rem;
      }
    }

    &__item {
      display: flex;
      gap: 0.75rem;
    }

    &__item--last &__rail-line {
      display: none;
    }

    &__rail {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;
      flex-shrink: 0;
      width: 28px;
    }

    &__rail-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 50%;
      color: var(--slate-700);
      flex-shrink: 0;
    }

    &__rail-line {
      flex: 1;
      width: 1px;
      min-height: 0.5rem;
      background: var(--slate-300);
    }

    &__content {
      flex: 1;
      min-width: 0;
      padding-bottom: 1.25rem;
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-height: 28px;
      flex-wrap: wrap;
    }

    &__row-spacer {
      flex: 1;
    }

    &__title {
      font-size: 0.8125rem;
      font-weight: 500;
      line-height: 1.25rem;
      color: var(--slate-900);
    }

    &__meta {
      font-size: 0.8125rem;
      line-height: 1.25rem;
      color: var(--slate-600);
    }

    &__time {
      font-size: 0.6875rem;
      font-weight: 300;
      line-height: 1.25rem;
      color: var(--slate-500);
      white-space: nowrap;
    }

    &__author-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--brand-100);
      color: var(--brand-600);
      font-size: 0.5rem;
      font-weight: 600;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      height: 18px;
      padding: 0 0.375rem;
      border-radius: 4px;
      background: var(--slate-100);
      border: 1px solid var(--slate-200);
      font-size: 0.625rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--slate-700);

      &--positive {
        background: var(--green-50);
        border-color: var(--green-300);
        color: var(--green-700);
      }
      &--negative {
        background: var(--red-50);
        border-color: var(--red-100);
        color: var(--red-600);
      }
      &--no_answer {
        background: var(--amber-50);
        border-color: var(--amber-200);
        color: var(--amber-700);
      }
    }

    &__link {
      padding: 0;
      border: none;
      background: none;
      font-size: 0.8125rem;
      font-weight: 500;
      line-height: 1.25rem;
      color: var(--brand-600);
      cursor: pointer;

      @include hover {
        &:hover {
          text-decoration: underline;
        }
      }
    }

    &__delete {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: none;
      border-radius: 4px;
      background: none;
      color: var(--slate-400);
      cursor: pointer;
      opacity: 0;
      transition:
        opacity 0.15s ease,
        color 0.15s ease;

      @include hover {
        &:hover {
          color: var(--red-600);
          background: var(--red-50);
        }
      }

      &:focus-visible {
        opacity: 1;
      }
    }

    &__item:hover &__delete {
      opacity: 1;
    }

    @media (pointer: coarse) {
      &__delete {
        opacity: 1;
      }
    }

    &__card {
      margin-top: 0.5rem;
      padding: 0.75rem 1rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 4px;
    }

    &__card-subject {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.25rem;
      color: var(--slate-900);
      white-space: pre-wrap;
      word-break: break-word;

      &--strong {
        font-weight: 500;
      }
    }

    &__card-text {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.25rem;
      color: var(--slate-700);
      white-space: pre-wrap;
      word-break: break-word;
      @include ellipsis(6);
    }

    &__card-subject + &__card-text {
      margin-top: 0.25rem;
    }

    &__images {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    &__image-link {
      display: block;
      width: 96px;
      height: 96px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid var(--slate-200);
        background: white;
      }
    }

    &__card-actions {
      display: flex;
      gap: 1rem;
      margin-top: 0.5rem;
    }

    // Modal styles
    &__modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1100;
    }

    &__modal {
      background: white;
      border-radius: 0.75rem;
      width: 90%;
      max-width: 500px;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid var(--slate-200);

      h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
      }

      button {
        background: none;
        border: none;
        padding: 0.25rem;
        cursor: pointer;
        color: var(--slate-400);

        &:hover {
          color: var(--slate-600);
        }
      }
    }

    &__modal-body {
      padding: 1.5rem;
      overflow-y: auto;
    }

    &__modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--slate-200);
    }

    &__form-group {
      margin-bottom: 1rem;

      label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-700);
        margin-bottom: 0.5rem;
      }

      textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid var(--slate-200);
        border-radius: 0.5rem;
        font-size: 0.875rem;
        resize: vertical;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: var(--brand-400);
        }
      }
    }

    &__type-buttons {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__type-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: var(--slate-300);
      }

      &--active {
        background: var(--brand-50);
        border-color: var(--brand-400);
        color: var(--brand-700);
      }
    }

    &__outcome-buttons {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__outcome-btn {
      padding: 0.375rem 0.75rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      font-size: 0.8125rem;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: var(--slate-300);
      }

      &--active {
        &.deal-activities__outcome-btn--positive {
          background: var(--green-100);
          border-color: var(--green-400);
          color: var(--green-700);
        }

        &.deal-activities__outcome-btn--neutral {
          background: var(--slate-100);
          border-color: var(--slate-400);
          color: var(--slate-700);
        }

        &.deal-activities__outcome-btn--negative {
          background: var(--red-100);
          border-color: var(--red-400);
          color: var(--red-600);
        }

        &.deal-activities__outcome-btn--no_answer {
          background: var(--yellow-100);
          border-color: var(--yellow-400);
          color: var(--yellow-700);
        }
      }
    }
  }
</style>
